import { useState, useEffect, useMemo, useCallback } from "react";
import { Trash2, Pencil, Plus, RefreshCw, Reply, Check } from "lucide-react";
import axios from "axios";
import ConfirmModal from "../../components/ConfirmModal.jsx";

export default function AdminBlogManager() {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    author: "",
    category: "",
    content: "",
    excerpt: "",
    image: "",
  });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    commentId: null,
  });
  const [editIndex, setEditIndex] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ message: "", type: "", visible: false });
  const [activeTab, setActiveTab] = useState("blogs");
  const [comments, setComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [replyModal, setReplyModal] = useState({ open: false, commentId: null });
  const [replyText, setReplyText] = useState("");
  const itemsPerPage = 6;
  const commentsPerPage = 10;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => setAlert({ message: "", type: "", visible: false }), 3000);
  };

  // Fetch blogs from backend
  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/api/admin/blogs`, {
          method: "GET",
          credentials: "include", // send HttpOnly cookie
        });
        if (!res.ok) throw new Error("Failed to fetch blogs");
        const data = await res.json();
        setBlogs(data);
      } catch (err) {
        console.error(err);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [BASE_URL]);

  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

  const resetForm = () => {
    setForm({
      title: "",
      slug: "",
      author: "",
      category: "",
      content: "",
      excerpt: "",
      image: "",
      status: "draft",
    });
    setEditIndex(null);
  };

  // Handle create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editIndex !== null ? "PUT" : "POST";
      const id = editIndex !== null ? blogs[editIndex]._id : "";

      // Prepare blog payload as plain object
      let blogPayload = {
        title: form.title || "",
        slug: form.slug || "",
        author: form.author || "",
        category: form.category || "",
        excerpt: form.excerpt || "",
        content: form.content || "",
        status: form.status || "draft",
      };

      // If a new image file is selected, upload it first
      if (form.image instanceof File) {
        const uploadData = new FormData();
        uploadData.append("image", form.image);

        const uploadRes = await fetch(`${BASE_URL}/api/upload/blog`, {
          method: "POST",
          body: uploadData,
        });

        if (!uploadRes.ok) throw new Error("Failed to upload image");
        const uploaded = await uploadRes.json();

        blogPayload.image = uploaded.imageHighRes; // main image path
        blogPayload.thumbnail = uploaded.image;    // thumbnail path
      } else if (typeof form.image === "string" && form.image.trim()) {
        // Existing image paths
        blogPayload.image = form.image;
        blogPayload.thumbnail = form.thumbnail || form.image; // fallback
      } else {
        throw new Error("Image is required");
      }

      // Send blogPayload to create/update endpoint
      const res = await fetch(`${BASE_URL}/api/admin/blogs/${id}`, {
        method,
        credentials: "include", // send HttpOnly cookies
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(blogPayload),
      });

      if (!res.ok) throw new Error("Failed to save blog post");
      const savedBlog = await res.json();
      showAlert("Blog post saved successfully", "success");

      // Update frontend state
      let updatedBlogs;
      if (editIndex !== null) {
        updatedBlogs = [...blogs];
        updatedBlogs[editIndex] = savedBlog;
      } else {
        updatedBlogs = [savedBlog, ...blogs];
      }

      setBlogs(updatedBlogs);
      resetForm();
      setShowForm(false);
      setCurrentPage(1);
      showAlert(editIndex !== null ? "Blog post updated successfully" : "Blog post created successfully", "success");

    } catch (err) {
      console.error(err);
      showAlert(err.message || "An error occurred while saving the blog post", "error");
    }
  };

  // Handle delete
  const handleDelete = async (index) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this blog post?");
    if (!confirmDelete) return;
    const id = blogs[index]._id;
    try {
      const res = await fetch(`${BASE_URL}/api/admin/blogs/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete blog post");
      const updated = [...blogs];
      updated.splice(index, 1);
      setBlogs(updated);
      showAlert("Blog post deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showAlert("Error deleting blog post", "error");
    }
  };

  // Edit
  const handleEdit = (index) => {
    setForm(blogs[index]);
    setEditIndex(index);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/comments/admin/all`, {
        withCredentials: true,
      });
      setComments(res.data);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    } finally {
      setLoadingComments(false);
    }
  }, [BASE_URL]);

  const handleApproveComment = async (id) => {
    try {
      await axios.put(`${BASE_URL}/api/comments/${id}/approve`, {}, { withCredentials: true });
      setComments((prev) =>
        prev.map((c) => (c._id === id ? { ...c, approved: true } : c))
      );
      showAlert("Comment approved successfully!", "success");
    } catch (err) {
      console.error("Approve failed:", err);
      showAlert("Failed to approve comment", "error");
    }
  };

  const handleDeleteComment = (id) => {
    setConfirmModal({ open: true, commentId: id });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/api/comments/${confirmModal.commentId}`, {
        withCredentials: true,
      });

      setComments((prev) =>
        prev.filter((c) => c._id !== confirmModal.commentId)
      );

      setConfirmModal({ open: false, commentId: null });
      showAlert("Comment deleted successfully!", "success");
    } catch (err) {
      console.error("Delete failed:", err);
      showAlert("Failed to delete comment", "error");
    }
  };

  const handleReply = (id) => {
    setReplyModal({ open: true, commentId: id });
    setReplyText(""); // reset input each time
  };

  const confirmReply = async () => {
    if (!replyText.trim()) {
      showAlert("Please enter a reply before submitting.", "error");
      return;
    }

    try {
      const res = await fetch(`${BASE_URL}/api/comments/${replyModal.commentId}/reply`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reply: replyText }),
      });

      if (!res.ok) throw new Error("Failed to reply");

      const data = await res.json();

      setComments((prev) =>
        prev.map((c) =>
          c._id === replyModal.commentId
            ? { ...data.comment, postId: c.postId }
            : c
        )
      );

      showAlert("Reply saved successfully!", "success");
      setReplyModal({ open: false, commentId: null });
      setReplyText("");
    } catch (err) {
      console.error("Failed to reply:", err);
      showAlert("Failed to save reply", "error");
    }
  };

  // Auto-fetch when tab switches
  useEffect(() => {
    if (activeTab === "comments") fetchComments();
  }, [activeTab, fetchComments]);

  // Filtering & pagination
  const filteredBlogs = useMemo(() => {
    return blogs.filter((blog) => {
      const matchesSearch =
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = filterCategory ? blog.category === filterCategory : true;
      return matchesSearch && matchesCategory;
    });
  }, [blogs, searchTerm, filterCategory]);

  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalCommentsPages = Math.ceil(comments.length / commentsPerPage);

  const startIndex = (currentPage - 1) * commentsPerPage;
  const currentComments = comments.slice(startIndex, startIndex + commentsPerPage);

  const categories = Array.from(
    new Set(blogs.map((b) => b.category).filter(Boolean))
  );

  const commentCounts = comments.reduce((acc, c) => {
    const id = c.postId?._id || c.postId;
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <div className="p-6">Loading blog manager...</div>;

  return (
    <div>
      {/* ✅ Banner Notification */}
      {alert.visible && (
        <div
          className={`mb-4 px-4 py-3 rounded text-sm ${
            alert.type === "success"
              ? "bg-green-100 text-green-800"
              : alert.type === "error"
              ? "bg-red-100 text-red-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {alert.message}
        </div>
      )}

      {/* ====== MANAGEMENT TABS ====== */}
      <div className="flex justify-center gap-4 mt-6 mb-6">
        <button
          onClick={() => setActiveTab("blogs")}
          className={`px-4 py-2 rounded font-semibold ${
            activeTab === "blogs" ? "bg-gray-800 text-white" : "bg-gray-200"
          }`}
        >
          Manage Blogs
        </button>
        <button
          onClick={() => setActiveTab("comments")}
          className={`px-4 py-2 rounded font-semibold ${
            activeTab === "comments" ? "bg-gray-800 text-white" : "bg-gray-200"
          }`}
        >
          Manage Comments
        </button>
      </div>

      {/* ====== BLOGS TAB ====== */}
      {activeTab === "blogs" && (
        <>
          <button
            onClick={() => {
              setShowForm((show) => !show);
              if (!showForm) resetForm();
              setEditIndex(null);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="mb-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
          >
            {showForm ? "Hide Form" : <><Plus size={20} /> Add New Blog Post</>}
          </button>

          {showForm && (
            <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow mb-10">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-1 font-semibold">Title</label>
                  <input
                    required
                    type="text"
                    placeholder="Title"
                    className="border p-2 rounded w-full"
                    value={form.title}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        title: newTitle,
                        slug: generateSlug(newTitle),
                      }));
                    }}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Slug</label>
                  <input
                    required
                    type="text"
                    placeholder="Slug"
                    className="border p-2 rounded w-full"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Author</label>
                  <input
                    required
                    type="text"
                    placeholder="Author"
                    className="border p-2 rounded w-full"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Category</label>
                  <input
                    type="text"
                    placeholder="Category"
                    className="border p-2 rounded w-full"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-1 font-semibold">Excerpt</label>
                <textarea
                  placeholder="Excerpt (short summary)"
                  rows="3"
                  className="border p-2 rounded w-full"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                ></textarea>
              </div>

              <div className="flex flex-col md:flex-row gap-4 mb-4">
                {/* Image Field */}
                <div className="flex-1">
                  <label className="block mb-1 font-semibold">Image</label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                    className="border p-2 rounded w-full"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setForm((prev) => ({ ...prev, image: file }));
                      }
                    }}
                  />
                  {form.image && (
                    <img
                      src={
                        form.image instanceof File
                          ? URL.createObjectURL(form.image)
                          : form.image
                      }
                      alt="Preview"
                      className="mt-2 h-32 w-auto rounded object-cover border"
                    />
                  )}
                </div>

                {/* Status Field */}
                <div className="flex-1">
                  <label className="block mb-1 font-semibold">Status</label>
                  <select
                    className="border p-2 rounded w-full"
                    value={form.status || "draft"}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="unpublished">Unpublished</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block mb-1 font-semibold">Content</label>
                <textarea
                  placeholder="Content"
                  rows="6"
                  className="border p-2 rounded w-full"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                ></textarea>
              </div>

              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-500"
                >
                  {editIndex !== null ? "Update Blog Post" : "Add Blog Post"}
                </button>

                <button
                  type="button"
                  className="bg-gray-300 text-gray-800 px-6 py-2 rounded hover:bg-gray-400"
                  onClick={() => {
                    setForm({
                      title: "",
                      slug: "",
                      author: "",
                      category: "",
                      excerpt: "",
                      content: "",
                      image: "",
                      status: "draft",
                    });
                    setEditIndex(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <div>
              <label className="mr-2 font-semibold">Filter by Category:</label>
              <select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="border p-2 rounded"
              >
                <option value="">All</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="search"
              placeholder="Search by title, author, category..."
              className="border p-2 rounded w-full md:w-64"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
            {paginatedBlogs.length > 0 ? (
              paginatedBlogs.map((blog, index) => (
                <div
                  key={index}
                  className="bg-white p-4 rounded shadow flex flex-col gap-2 relative"
                >
                  {/* Status Badge */}
                  {blog.status && (
                    <span
                      className={`absolute top-6 left-6 px-2 py-1 text-xs font-semibold rounded ${
                        blog.status === "published"
                          ? "bg-green-100 text-green-800"
                          : blog.status === "draft"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {blog.status}
                    </span>
                  )}

                  {blog.image && (
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-32 object-cover rounded"
                    />
                  )}

                  <div>
                    <p className="font-bold">{blog.title}</p>
                    {blog.excerpt && (
                      <p className="text-sm text-gray-600">{blog.excerpt}</p>
                    )}
                    <p className="text-sm text-gray-400 mt-2 italic">
                      {blog.author} · {blog.category} · Comments({commentCounts[blog._id] || 0}) · Likes({blog.likes || 0})
                    </p>
                  </div>

                  <div className="flex justify-between mt-2">
                    <button
                      onClick={() => handleEdit(blogs.indexOf(blog))}
                      className="text-blue-600 flex items-center gap-1"
                    >
                      <Pencil size={16} /> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(blogs.indexOf(blog))}
                      className="text-red-600 flex items-center gap-1"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center col-span-full">No blog posts found.</p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded border ${
                    currentPage === i + 1 ? "bg-blue-600 text-white" : ""
                  }`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* ====== COMMENTS TAB ====== */}
      {activeTab === "comments" && (
        <div className="bg-white p-6 rounded shadow mt-8">
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row justify-between sm:items-center items-start gap-3 mb-4">
            <h2 className="text-xl font-bold text-gray-800">All Comments</h2>

            {/* Reload Icon Button */}
            <button
              onClick={fetchComments}
              className={`flex items-center justify-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded hover:bg-blue-200 text-sm w-full sm:w-auto text-center transition ${
                loadingComments ? "opacity-80 cursor-wait" : ""
              }`}
              title="Reload Comments"
              disabled={loadingComments}
            >
              <RefreshCw
                size={16}
                className={loadingComments ? "animate-spin text-blue-700" : ""}
              />
              {loadingComments ? "Refreshing..." : "Refresh Comments"}
            </button>
          </div>

          {loadingComments ? (
            <p className="text-center text-gray-500 py-6">Loading comments...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="p-2 text-left">Post</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Comment</th>
                    <th className="p-2 text-left">Approved</th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-6 text-gray-500">
                        No comments found.
                      </td>
                    </tr>
                  ) : (
                    currentComments.map((comment) => (
                      <tr key={comment._id} className="border-t hover:bg-gray-50">
                        <td className="p-2 text-gray-700">
                          {comment.postId ? (
                            <a
                              href={`/blog/${comment.postId.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                              title={comment.postId.title} // full title on hover
                            >
                              {comment.postId.title.length > 25
                                ? `${comment.postId.title.slice(0, 25)}...`
                                : comment.postId.title}
                            </a>
                          ) : (
                            <span className="italic text-gray-400">Untitled</span>
                          )}
                        </td>
                        <td className="p-2">{comment.name}</td>
                        {/* Comment Text (truncated with modal) */}
                        <td className="p-2 text-gray-700 cursor-pointer hover:text-blue-600">
                          {comment.text.length > 25 ? (
                            <span onClick={() => setSelectedComment(comment.text)}>
                              {comment.text.slice(0, 25)} ...
                            </span>
                          ) : (
                            comment.text
                          )}
                        </td>
                        <td className="p-2">
                          {comment.approved ? (
                            <span className="text-green-600 font-semibold">Yes</span>
                          ) : (
                            <span className="text-red-600 font-semibold">No</span>
                          )}
                        </td>
                        <td className="p-2 text-gray-500">
                          {new Date(comment.createdAt).toLocaleString()}
                        </td>
                        <td className="p-2 flex flex-wrap gap-4">
                          {!comment.approved && (
                            <button
                              onClick={() => handleApproveComment(comment._id)}
                              className="text-green-600 hover:underline flex items-center gap-1"
                            >
                              <Check size={14} /> Approve
                            </button>
                          )}
                          <button
                            onClick={() => handleReply(comment._id)}
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Reply size={14} /> Reply
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment._id)}
                            className="text-red-600 hover:underline flex items-center gap-1"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {totalCommentsPages > 1 && (
                <div className="flex justify-center space-x-2 mt-4">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border disabled:opacity-50"
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded border ${
                        currentPage === i + 1 ? "bg-blue-600 text-white" : ""
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Modal for Full Comment */}
              {selectedComment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                    <h2 className="text-lg font-semibold mb-4">Full Comment</h2>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedComment}</p>
                    <div className="mt-6 text-right">
                      <button
                        onClick={() => setSelectedComment(null)}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Delete Comment"
        message="Are you sure you want to permanently delete this comment? This action cannot be undone."
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        onClose={() => setConfirmModal({ open: false, commentId: null })}
        onConfirm={confirmDelete}
      />

      {replyModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">Reply to Comment</h2>

            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              rows={4}
              className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />

            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => setReplyModal({ open: false, commentId: null })}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={confirmReply}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
              >
                Send Reply
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
